/**
 * The message list the chat sends to the AI, with and without a conversation summary - kept free of React,
 * Dexie and the network so it can be unit tested (`npm run test:ai-chat`).
 *
 * Bug this replaces: after the conversation was summarised, the list sent on EVERY later turn was the frozen
 * `compressedMessages` of the moment of summarising (summary + the 5 messages then), so the user's new message
 * was never in it and the AI answered an old question. The list is now always: the summary, followed by every
 * message after the part the summary covers - the newest one included.
 */

import type { ChatAttachment, ChatContext, ChatMessage, ConversationFocus } from "./useAiChat"

/** What `tryCompressConversation` remembers about a summary (the type lives in conversationCompression.ts). */
export interface SummaryInfo {
  isSummarized: boolean
  summary?: string
  intents?: string[]
  completedActions?: string[]
  /** How many messages (from the start of the conversation) the summary covers. */
  summarizedCount?: number
}

/** One message as POST /ai/chat takes it. */
export interface ApiChatMessage {
  role: string
  content: string
  attachments?: ChatAttachment[]
  toolCalls?: ChatMessage["toolCalls"]
  toolResults?: ChatMessage["toolResults"]
}

/** The summary as the first thing the AI reads. */
export function summaryText(info: SummaryInfo): string {
  return `## Previous Summary\n${info.summary ?? ""}\n\n### Intents: ${info.intents?.join(", ") || "N/A"}\n### Completed: ${info.completedActions?.join(", ") || "N/A"}`
}

/**
 * The messages to send. Without a summary: all of them. With one: the summary, then everything after the
 * summarised part. Earlier messages are sent as plain text (no files, no tool rounds); the LAST message keeps its
 * attachments and tool results - it is the one being answered.
 *
 * The summary is folded into the first remaining message when that is a user message (the model's turns must
 * alternate), else it goes in front as its own user message.
 */
export function buildRequestMessages(
  messages: ChatMessage[],
  info: SummaryInfo,
  toApi: (m: ChatMessage) => ApiChatMessage,
): ApiChatMessage[] {
  const covered = info.summarizedCount ?? 0
  // Not summarised, or the list no longer matches the summary (messages were removed): send everything.
  if (!info.isSummarized || !info.summary || covered <= 0 || covered >= messages.length) {
    return messages.map(toApi)
  }

  const tail = messages.slice(covered)
  const last = tail.length - 1
  const out = tail.map((m, i) => {
    const full = toApi(m)
    return i === last ? full : { role: full.role, content: full.content }
  })

  const summary = summaryText(info)
  const first = out[0]
  if (first && first.role === "user") {
    out[0] = { ...first, content: `${summary}\n\n${first.content}` }
  } else {
    out.unshift({ role: "user", content: summary })
  }
  return out
}

/**
 * The headers that tell the server which message a POST /ai/chat belongs to. The Worker counts the message
 * BEFORE reading the body (an over-quota user is refused without uploading anything), so the turn travels in
 * headers: `X-AI-Turn-Id` (one per user message) and `X-AI-Continuation: 1` for every round after the first of
 * that message's tool loop. A first round is never a continuation, so a resent first round is a new message.
 */
export function chatTurnHeaders(turnId: string, round: number): Record<string, string> {
  return { "X-AI-Turn-Id": turnId, "X-AI-Continuation": round > 0 ? "1" : "0" }
}

/**
 * The body of one POST /ai/chat. `turnId` is one id per USER MESSAGE: every round of that message's tool loop
 * sends the same one, and the server counts a message only on the first call of a new turnId (and refuses more
 * rounds than the loop has). It is a fresh uuid for each message the user sends - a retry of a failed message is a
 * new message - and never reused across messages.
 */
export function buildChatRequestBody(args: {
  messages: ApiChatMessage[]
  conversationId: string | null
  turnId: string
  memoryMaster?: string
  context?: ChatContext
  focus?: ConversationFocus
}): Record<string, unknown> {
  const { context, focus } = args
  return {
    messages: args.messages,
    conversationId: args.conversationId,
    turnId: args.turnId,
    memoryMaster: args.memoryMaster || undefined,
    context: context
      ? {
          viewId: context.viewId,
          containerId: context.containerId ?? undefined,
          containerTitle: context.containerTitle,
          containerType: context.containerType,
          activeTaskId: context.activeTaskId ?? undefined,
          activeTaskTitle: context.activeTaskTitle,
          activeTaskNote: context.activeTaskNote,
          activeTaskTags: context.activeTaskTags,
          activeTaskDueAt: context.activeTaskDueAt,
          activeTaskParentPath: context.activeTaskParentPath,
        }
      : undefined,
    focus:
      focus && (focus.lastEntity || focus.impliedContainer)
        ? { lastEntity: focus.lastEntity, impliedContainer: focus.impliedContainer, recentEntities: focus.recentEntities }
        : undefined,
  }
}
